import { useQuery } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { UserSearchResultResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Find-players search (GET /social/users/search?q=), Player Slice 4b. Returns the
 * server's PUBLIC-SAFE results VERBATIM ({user_id, display_name, avatar_url}) —
 * never a bare uid, never a client-derived relationship state. The page renders
 * each result by display_name and overlays the relationship action (Friends /
 * Requested / Add) as a VIEW of the separately-loaded friends + requests reads.
 *
 * The caller passes the DEBOUNCED, TRIMMED query. The query only RUNS when that
 * value is ≥ 2 chars (`enabled`) — the server requires q (min_length=2) and the
 * client guards a blank query (no fetch). A shorter / blank query keeps the
 * query idle (no fetch, no stale results), so the page shows its honest prompt
 * rather than a fabricated empty list. Keyed by the query so each distinct
 * search gets its own cache slot. No client sort, no derived state.
 */
export function useUserSearch(query: string) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;
  return useQuery<UserSearchResultResponse[]>({
    queryKey: queryKeys.social.userSearch(trimmed),
    queryFn: () => socialApi.searchUsers(trimmed),
    enabled,
  });
}
